import React, {
  useEffect,
  useState,
  useMemo,
  useRef,
  useCallback,
} from 'react';
import { Link } from 'react-router-dom';
import styles from './Network.module.css';
import { getAllProfiles } from '../services/skillProfileService';
import ProfileCard from '../components/ProfileCard';

const Network = () => {
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTags, setSelectedTags] = useState([]);
  const [openCategoryDropdown, setOpenCategoryDropdown] = useState(null); // 'profession' | 'hobby' | 'skill' | null
  const categoriesRef = useRef(null);

  useEffect(() => {
    let isMounted = true;

    const fetchProfiles = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await getAllProfiles();
        if (!isMounted) return;
        setProfiles(Array.isArray(data) ? data : []);
      } catch (err) {
        if (!isMounted) return;
        setError('Errore nel caricamento dei profili. Riprova più tardi.');
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchProfiles();

    return () => {
      isMounted = false;
    };
  }, []);

  const publicProfiles = useMemo(
    () => profiles.filter((p) => p.isPublic),
    [profiles]
  );

  const filteredProfiles = useMemo(() => {
    let result = publicProfiles;

    if (searchQuery.trim()) {
      const q = searchQuery.trim().toLowerCase();
      result = result.filter((p) => {
        const name = p.fullName || '';
        const bio = p.bio || '';
        const tagNames = (p.tags || []).map((t) => t.name).filter(Boolean);
        const matchesTag = tagNames.some((tagName) =>
          tagName.toLowerCase().includes(q)
        );
        return (
          name.toLowerCase().includes(q) ||
          bio.toLowerCase().includes(q) ||
          matchesTag
        );
      });
    }

    if (selectedTags.length > 0) {
      result = result.filter((p) => {
        const tags = p.tags || [];
        const tagNames = tags.map((t) => t.name).filter(Boolean);
        return selectedTags.some((tag) => tagNames.includes(tag));
      });
    }

    result = [...result].sort((a, b) =>
      (a.fullName || '').localeCompare(b.fullName || '')
    );

    return result;
  }, [publicProfiles, searchQuery, selectedTags]);

  const CATEGORY_LABELS = {
    profession: 'Professioni',
    hobby: 'Hobby',
    skill: 'Competenze',
  };

  const categoryCounts = useMemo(() => {
    const categories = ['profession', 'hobby', 'skill'];
    return categories.map((category) => {
      const tagIds = new Set();
      publicProfiles.forEach((p) => {
        (p.tags || []).forEach((t) => {
          if (t.category === category && (t.id || t.name)) {
            tagIds.add(t.id || t.name);
          }
        });
      });
      const tagCount = tagIds.size;
      return {
        category,
        label: CATEGORY_LABELS[category],
        tagCount,
      };
    });
  }, [publicProfiles]);

  // Tag unici per categoria (nome) per il pannello filtri
  const tagsByCategory = useMemo(() => {
    const byCat = { profession: [], hobby: [], skill: [] };
    const seen = { profession: new Set(), hobby: new Set(), skill: new Set() };
    publicProfiles.forEach((p) => {
      (p.tags || []).forEach((t) => {
        const { category: cat, name } = t;
        if (cat && name && !seen[cat].has(name)) {
          seen[cat].add(name);
          byCat[cat].push({ name, id: t.id || name });
        }
      });
    });
    ['profession', 'hobby', 'skill'].forEach((c) =>
      byCat[c].sort((a, b) => a.name.localeCompare(b.name))
    );
    return byCat;
  }, [publicProfiles]);

  const toggleTag = useCallback((tagName) => {
    setSelectedTags((prev) =>
      prev.includes(tagName)
        ? prev.filter((t) => t !== tagName)
        : [...prev, tagName]
    );
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (categoriesRef.current && !categoriesRef.current.contains(e.target)) {
        setOpenCategoryDropdown(null);
      }
    };
    if (openCategoryDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [openCategoryDropdown]);

  const scrollToExplore = useCallback(() => {
    document
      .getElementById('esplora-network')
      ?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  return (
    <>
      <section className={styles.hero} aria-label="Hero Network">
        <div
          className={styles.heroBg}
          style={{
            backgroundImage: `url(${process.env.PUBLIC_URL || ''}/images/network-hero.png)`,
          }}
        />
        <div className={styles.heroOverlay} />
        <div className={styles.heroInner}>
          <h1 className={styles.heroTitle}>Network dei Soci CIL</h1>
          <p className={styles.heroDescription}>
            Condividi competenze, professioni e passioni. Trova chi può
            collaborare con te.
          </p>
          <div className={styles.heroButtons}>
            <Link to="/account/network" className={styles.heroBtnPrimary}>
              Aggiungi il tuo profilo
            </Link>
            <button
              type="button"
              className={styles.heroBtnSecondary}
              onClick={scrollToExplore}
            >
              Esplora il network
            </button>
          </div>
        </div>
      </section>

      <div className={styles.page}>
        <header className={styles.header} id="esplora-network">
          <div className={styles.filters}>
            <div className={styles.searchWrap}>
              <span className={styles.searchIcon} aria-hidden="true">
                🔍
              </span>
              <input
                type="search"
                className={styles.searchInput}
                placeholder="Cerca per nome, descrizione, competenze, professioni…"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                aria-label="Cerca soci per nome, descrizione, competenze o professioni"
              />
              {searchQuery && (
                <button
                  type="button"
                  className={styles.searchClear}
                  onClick={() => setSearchQuery('')}
                  aria-label="Cancella ricerca"
                >
                  ×
                </button>
              )}
            </div>
          </div>
        </header>

        {!loading && !error && (
          <section
            className={styles.categoriesSection}
            aria-label="Categorie tag"
            ref={categoriesRef}
          >
            <div className={styles.categoriesRow}>
              <ul className={styles.categoriesList}>
                {categoryCounts.map(({ category, label, tagCount }) => (
                  <li key={category} className={styles.categoryItemWrap}>
                    <button
                      type="button"
                      className={`${styles.categoryItem} ${styles[category]} ${openCategoryDropdown === category ? styles.categoryItemOpen : ''}`}
                      onClick={() =>
                        setOpenCategoryDropdown((prev) =>
                          prev === category ? null : category
                        )
                      }
                      aria-expanded={openCategoryDropdown === category}
                      aria-haspopup="true"
                      aria-label={`${label}: ${tagCount} tag. Clicca per vedere l'elenco`}
                    >
                      <span className={styles.categoryCount}>{tagCount}</span>
                      <span className={styles.categoryLabel}>{label}</span>
                      <span className={styles.categoryChevron} aria-hidden>
                        ▼
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
              <div className={styles.membersSection}>
                <div className={`${styles.categoryItem} ${styles.members}`}>
                  <span className={styles.categoryCount}>
                    {publicProfiles.length}
                  </span>
                  <span className={styles.categoryLabel}>Soci</span>
                </div>
              </div>
            </div>
            {openCategoryDropdown && (
              <div className={styles.categoryListBlock} role="listbox">
                {tagsByCategory[openCategoryDropdown].length === 0 ? (
                  <p className={styles.categoryListEmpty}>
                    Nessun tag in questa categoria
                  </p>
                ) : (
                  <ul className={styles.categoryListTags}>
                    {tagsByCategory[openCategoryDropdown].map(({ name }) => (
                      <li key={name}>
                        <button
                          type="button"
                          role="option"
                          aria-selected={selectedTags.includes(name)}
                          className={`${styles.categoryListTag} ${selectedTags.includes(name) ? styles.filterTagSelected : ''}`}
                          onClick={() => toggleTag(name)}
                        >
                          {name}
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </section>
        )}

        {loading && (
          <div className={styles.state}>
            <p>Caricamento profili…</p>
          </div>
        )}

        {error && !loading && (
          <div className={styles.stateError}>
            <p>{error}</p>
          </div>
        )}

        {!loading && !error && filteredProfiles.length === 0 && (
          <div className={styles.state}>
            <p>Nessun profilo pubblico disponibile al momento.</p>
          </div>
        )}

        {!loading && !error && filteredProfiles.length > 0 && (
          <section className={styles.grid}>
            {filteredProfiles.map((profile, index) => (
              <ProfileCard
                key={profile.documentId ?? profile.id ?? `profile-${index}`}
                profile={profile}
                onClick={() => {
                  const targetId = profile.documentId || profile.id;
                  if (targetId) {
                    window.location.assign(`/network/${targetId}`);
                  }
                }}
              />
            ))}
          </section>
        )}
      </div>
    </>
  );
};

export default Network;
